import React, { useState, useEffect, useRef } from 'react';
import './Dictation.css';
import SettingsModal from '../../components/SettingsModal';
import { dictationConfig } from '../../config/settingsConfig';

// 导入图片资源
import logoImage from '../../static/image/白字logo.png';
import playIcon from '../../static/image/播放.png';

const Dictation = () => {
  const [selectedWords, setSelectedWords] = useState([]);
  const selectedWordsRef = useRef([]); // 使用ref跟踪selectedWords
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 听写状态管理
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // 自动播放状态
  const [isAutoPlaying, setIsAutoPlaying] = useState(false); // 默认暂停
  const isAutoPlayingRef = useRef(false); // 使用ref跟踪自动播放状态
  
  // 听写设置弹窗状态
  const [showDictationSettings, setShowDictationSettings] = useState(false);
  
  // 结算画面状态
  const [showResults, setShowResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  
  // 全局音频管理
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioPlaybackCancelled, setAudioPlaybackCancelled] = useState(false);
  
  // 使用ref来跟踪播放状态，避免React状态更新延迟
  const isPlayingRef = React.useRef(false);
  const playbackIdRef = useRef(0); // 播放ID，用于识别最新的播放操作
  const navigationTimeoutRef = useRef(null); // 导航防抖定时器
  const currentWordIndexRef = useRef(0); // 使用ref跟踪当前单词索引
  
  // 显示控制状态
  const [showChinese, setShowChinese] = useState(false);
  const [showPhonetic, setShowPhonetic] = useState(false);
  
  // 倒计时状态
  const [countdown, setCountdown] = useState(0); // 0表示未开始倒计时
  const countdownTimerRef = useRef(null);
  
  // 听写设置
  const [dictationSettings, setDictationSettings] = useState({
    playCount: 2,      // 播放次数，默认2次
    pauseTime: 1,      // 留白时长，默认1倍
    playSpeed: 'normal' // 播放语速，默认正常
  });
  const dictationSettingsRef = useRef(dictationSettings); // 使用ref跟踪最新设置

  // 创建音频引用，用于在清理时访问
  const currentAudioRef = useRef(null);
  
  // 同步 currentAudio 到 ref
  useEffect(() => {
    currentAudioRef.current = currentAudio;
  }, [currentAudio]);
  
  // 设置页面特殊标识，隐藏装饰图案
  useEffect(() => {
    // 添加特殊class到body
    document.body.classList.add('dictation-page');
    
    // 组件卸载时移除class和清理定时器
    return () => {
      document.body.classList.remove('dictation-page');
      
      // 停止所有音频播放
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      }
      
      // 停止自动播放
      isAutoPlayingRef.current = false;
      isPlayingRef.current = false;
      
      // 增加播放ID，使所有进行中的异步播放失效
      playbackIdRef.current++;
      
      // 清理导航防抖定时器
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      
      // 清理倒计时定时器
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
      }
      
      // 组件卸载时清除 sessionStorage 中的听写数据和访问标记
      sessionStorage.removeItem('dictationData');
      sessionStorage.removeItem('dictationPageVisited');
    };
  }, []); // 空依赖数组，只在挂载和卸载时执行

  // 同步 currentWordIndex 到 ref
  useEffect(() => {
    currentWordIndexRef.current = currentWordIndex;
  }, [currentWordIndex]);

  // 同步 dictationSettings 到 ref
  useEffect(() => {
    dictationSettingsRef.current = dictationSettings;
  }, [dictationSettings]);

  // 同步 selectedWords 到 ref
  useEffect(() => {
    selectedWordsRef.current = selectedWords;
  }, [selectedWords]);

  // 从 sessionStorage 获取选中的单词数据和设置（避免URL过长导致431错误）
  useEffect(() => {
    const initializeDictation = async () => {
      try {
        // 优先从 sessionStorage 获取数据
        const sessionData = sessionStorage.getItem('dictationData');
        let parsedData = null;
        let wordsData = [];
        let settings = null;
        
        // 检查是否是页面刷新
        const isPageRefresh = sessionStorage.getItem('dictationPageVisited') === 'true';
        
        if (sessionData) {
          parsedData = JSON.parse(sessionData);
          wordsData = parsedData.words || [];
          settings = parsedData.settings;
          // 不要立即删除，支持页面刷新
        } else {
          // 兼容旧的 URL 参数方式
          const urlParams = new URLSearchParams(window.location.search);
          let dataParam = urlParams.get('data');
          
          if (dataParam) {
            // 新格式：包含单词数据和设置
            parsedData = JSON.parse(decodeURIComponent(dataParam));
            wordsData = parsedData.words || [];
            settings = parsedData.settings;
          } else {
            // 兼容旧格式：仅单词数据
            const wordsParam = urlParams.get('words');
            if (wordsParam) {
              wordsData = JSON.parse(decodeURIComponent(wordsParam));
            }
          }
        }
        
        if (wordsData.length > 0) {
          setSelectedWords(wordsData);
          
          // 使用要应用的设置（如果有的话）
          const settingsToUse = settings || dictationSettings;
          if (settings) {
            setDictationSettings(settings);
            dictationSettingsRef.current = settings; // 立即更新 ref
          }
          
          // 标记页面已访问（用于检测刷新）
          if (!isPageRefresh) {
            sessionStorage.setItem('dictationPageVisited', 'true');
          }
          
          // 默认进入暂停状态，不自动播放
          setIsAutoPlaying(false);
          isAutoPlayingRef.current = false;
        } else {
          setError('未找到听写数据');
        }
      } catch (err) {
        console.error('解析听写数据失败:', err);
        setError('听写数据格式错误');
      } finally {
        setLoading(false);
      }
    };
    
    initializeDictation();
  }, []);

  // 播放单词音频
  const playWordAudio = async (word, customSettings = null) => {
    // 生成新的播放ID
    const currentPlaybackId = ++playbackIdRef.current;
    
    // 重置播放取消标志 - 防止之前的取消操作影响新播放
    setAudioPlaybackCancelled(false);
    
    const currentSettings = customSettings || dictationSettings;
    
    // 用于保存音频时长，供倒计时使用
    let audioDuration = 0;
    
    // 强制停止所有当前音频和重置状态
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    
    // 检查是否有音频资源
    if (!word.audio || !word.audio.res) {
      return;
    }
    
    // 检查播放是否已被取消
    if (currentPlaybackId !== playbackIdRef.current) {
      return;
    }
    
    // 确保播放状态已设置
    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      setIsPlaying(true);
    }
    
    try {
      const audioUrl = word.audio.res;
      const playCount = currentSettings.playCount;
      const pauseTimeMultiplier = currentSettings.pauseTime;
      
      // 检查音频URL是否可访问
      const testResponse = await fetch(audioUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`音频文件不可访问: ${testResponse.status} ${testResponse.statusText}`);
      }
      
      // 创建音频对象
      const audio = new Audio(audioUrl);
      
      // 设置当前音频对象
      setCurrentAudio(audio);
      
      // 设置播放速度
      const playbackRate = currentSettings.playSpeed === 'fast' ? 1.5 : 
                          currentSettings.playSpeed === 'slow' ? 0.75 : 1;
      audio.playbackRate = playbackRate;
      
      // 先加载音频以获取时长
      await new Promise((resolve, reject) => {
        audio.onloadedmetadata = () => {
          audio.playbackRate = playbackRate;
          resolve();
        };
        audio.onerror = reject;
        audio.load();
      });
      
      audioDuration = audio.duration; // 保存音频时长供倒计时使用
      // 留白时间 = 向上取整(音频时长) × 倍数
      const pauseTime = Math.ceil(audioDuration) * pauseTimeMultiplier * 1000;
      
      // 播放指定次数
      for (let i = 0; i < playCount; i++) {
        // 检查是否被取消或播放ID不匹配
        if (audioPlaybackCancelled || currentPlaybackId !== playbackIdRef.current) {
          break;
        }
        
        // 播放期间设置为"播放"状态（倒计时为-1表示播放中）
        setCountdown(-1);
        
        await new Promise((resolve, reject) => {
          audio.currentTime = 0;
          
          const onEnded = () => {
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = (error) => {
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
            reject(error);
          };
          
          audio.addEventListener('ended', onEnded);
          audio.addEventListener('error', onError);
          
          audio.playbackRate = playbackRate;
          audio.play().catch(reject);
        });
        
        // 每次播放后都添加留白时间（包括最后一次）
        if (pauseTime > 0 && !audioPlaybackCancelled && currentPlaybackId === playbackIdRef.current) {
          // 在留白期间显示倒计时（只在最后一次播放后显示）
          if (i === playCount - 1) {
            const countdownSeconds = Math.ceil(pauseTime / 1000);
            startCountdown(countdownSeconds);
          }
          
          // 在留白期间也要保持播放状态，但要能响应取消
          await new Promise(resolve => {
            const timeout = setTimeout(resolve, pauseTime);
            // 如果被取消，立即清除定时器
            const checkCancel = () => {
              if (audioPlaybackCancelled || currentPlaybackId !== playbackIdRef.current) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(checkCancel, 100);
              }
            };
            checkCancel();
          });
        }
      }
      
    } catch (error) {
      console.error('音频播放失败:', error);
    } finally {
      // 只有当前播放ID匹配时才重置状态，避免被新播放覆盖
      if (currentPlaybackId === playbackIdRef.current) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentAudio(null);
        setAudioPlaybackCancelled(false);
        setCountdown(0); // 清除倒计时/播放状态显示
        
        // 如果是自动播放模式，播放完成（包括留白）后直接进入下一个单词
        if (isAutoPlayingRef.current) {
          setTimeout(() => {
            if (isAutoPlayingRef.current) {
              autoPlayNext();
            }
          }, 100);
        }
      }
    }
  };

  // 停止当前音频播放
  const stopCurrentAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setAudioPlaybackCancelled(true);
    setCurrentAudio(null);
  };

  // 清除倒计时
  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  };

  // 开始倒计时（只用于显示，不额外增加等待时间）
  const startCountdown = (seconds) => {
    // 清除之前的倒计时
    clearCountdown();
    
    // 设置初始倒计时
    setCountdown(seconds);
    
    const runCountdown = (remaining) => {
      if (remaining > 0) {
        countdownTimerRef.current = setTimeout(() => {
          setCountdown(remaining - 1);
          runCountdown(remaining - 1);
        }, 1000);
      } else {
        // 倒计时结束
        setCountdown(0);
      }
    };
    
    runCountdown(seconds);
  };

  // 获取当前单词
  const getCurrentWord = () => {
    return selectedWords[currentWordIndex] || null;
  };

  // 自动播放下一个单词（内部使用，不停止自动播放）
  const autoPlayNext = () => {
    // 使用ref获取最新的索引和单词列表
    const currentIndex = currentWordIndexRef.current;
    const words = selectedWordsRef.current;
    
    if (currentIndex < words.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentWordIndex(newIndex);
      currentWordIndexRef.current = newIndex;
      
      // 立即设置播放状态
      isPlayingRef.current = true;
      setIsPlaying(true);
      
      setTimeout(() => {
        if (words[newIndex] && isAutoPlayingRef.current) {
          playWordAudio(words[newIndex], dictationSettingsRef.current);
        } else {
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
      }, 150);
    } else {
      // 播放到最后一个，停止自动播放并显示结算画面
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
      setTimeout(() => {
        setShowResults(true);
      }, 800);
    }
  };

  // 下一个单词（手动切换，停止自动播放）
  const nextWord = () => {
    // 停止自动播放
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    // 清除之前的导航定时器
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    // 如果当前有音频在播放，先停止
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    if (currentWordIndex < selectedWords.length - 1) {
      const newIndex = currentWordIndex + 1;
      setCurrentWordIndex(newIndex);
      
      // 立即设置播放状态，避免闪烁
      isPlayingRef.current = true;
      setIsPlaying(true);
      
      // 使用防抖机制，避免快速切换时重复播放
      navigationTimeoutRef.current = setTimeout(() => {
        if (selectedWords[newIndex]) {
          playWordAudio(selectedWords[newIndex], dictationSettings);
        } else {
          // 如果没有单词可播放，重置状态
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
      }, 150); // 150ms防抖延时
    }
  };

  // 结算按钮处理函数
  const handleSettle = () => {
    // 停止自动播放
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    // 停止当前音频
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    // 显示结算画面
    setShowResults(true);
  };

  // 上一个单词（手动切换，停止自动播放）
  const prevWord = () => {
    // 停止自动播放
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    // 清除之前的导航定时器
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    // 如果当前有音频在播放，先停止
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    if (currentWordIndex > 0) {
      const newIndex = currentWordIndex - 1;
      setCurrentWordIndex(newIndex);
      
      // 立即设置播放状态，避免闪烁
      isPlayingRef.current = true;
      setIsPlaying(true);
      
      // 使用防抖机制，避免快速切换时重复播放
      navigationTimeoutRef.current = setTimeout(() => {
        if (selectedWords[newIndex]) {
          playWordAudio(selectedWords[newIndex], dictationSettings);
        } else {
          // 如果没有单词可播放，重置状态
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
      }, 150); // 150ms防抖延时
    }
  };

  // 切换自动播放
  const toggleAutoPlay = () => {
    const newAutoPlayState = !isAutoPlaying;
    setIsAutoPlaying(newAutoPlayState);
    isAutoPlayingRef.current = newAutoPlayState;
    
    // 如果关闭自动播放，清除倒计时
    if (!newAutoPlayState) {
      clearCountdown();
    }
    
    // 如果开启自动播放且当前没有播放，立即开始播放当前单词
    if (newAutoPlayState && !isPlaying) {
      const currentWord = selectedWords[currentWordIndex];
      if (currentWord) {
        playWordAudio(currentWord, dictationSettings);
      }
    }
  };
  
  // 控制显示功能
  const handleToggleChinese = () => {
    setShowChinese(prev => !prev);
  };

  const handleTogglePhonetic = () => {
    setShowPhonetic(prev => !prev);
  };
  
  // 返回单词选择页面（使用浏览器返回功能）
  const handleBack = () => {
    window.history.back();
  };
  
  // 关闭结算弹窗
  const handleCloseResults = () => {
    setShowResults(false);
  };
  
  // 展示答案
  const handleShowAnswers = () => {
    setShowAnswers(true);
  };
  
  // 重新听写
  const handleRetry = () => {
    // 重置所有状态
    setShowResults(false);
    setShowAnswers(false);
    setCurrentWordIndex(0);
    currentWordIndexRef.current = 0;
    setIsPlaying(false);
    isPlayingRef.current = false;
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    setShowChinese(false);
    setShowPhonetic(false);
    
    // 停止当前音频
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
  };
  
  // 关闭答案显示，回到结算画面
  const handleCloseAnswers = () => {
    setShowAnswers(false);
  };
  
  // 打开听写设置弹窗
  const handleOpenDictationSettings = () => {
    setShowDictationSettings(true);
  };
  
  // 关闭听写设置弹窗
  const handleDictationSettingsClose = () => {
    setShowDictationSettings(false);
  };
  
  // 更新听写设置
  const handleDictationSettingsUpdate = (newSettings) => {
    setDictationSettings(newSettings);
    dictationSettingsRef.current = newSettings;
    setShowDictationSettings(false);
  };

  // 渲染加载状态
  if (loading) {
    return (
      <div className="dictation-container">
        <div className="dictation-loading">
          <div className="loading-spinner"></div>
          <p>正在准备听写...</p>
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <div className="dictation-container">
        <div className="dictation-error">
          <p>{error}</p>
          <button className="back-button" onClick={handleBack}>
            返回单词选择
          </button>
        </div>
      </div>
    );
  }

  const currentWord = getCurrentWord();

  return (
      <div className="dictation-container">
        {/* Logo区域 */}
        <div className="dictation-header">
          <img 
            src={logoImage} 
            alt="大鱼人机口语" 
            className="dictation-logo"
          />
        </div>
        
        {/* 第一部分 - 听写操作区域 (88%) */}
        <div className={`dictation-section-1 ${isPlaying ? 'playing' : ''}`}>
          {/* 顶部蓝色半圆文字 */}
          <div className="top-semicircle-text">
            {countdown === -1 ? '听' : countdown > 0 ? `${countdown}s` : '暂停'}
          </div>
          <div className="dictation-content">
            {/* 进度显示 */}
            <div className="dictation-progress">
              {currentWordIndex + 1}/{selectedWords.length}
            </div>

            {/* 写单词展示区域 */}
            <div className="dictation-writing-area">
              <div className="writing-lines">
                <div className="writing-line"></div>
                <div className="writing-line"></div>
                <div className="writing-line"></div>
                <div className="writing-line"></div>
              </div>
            </div>

            {/* 音标显示 */}
            <div className={`dictation-phonetic ${showPhonetic ? 'visible' : 'hidden'}`}>
              <span className="phonetic-text">
                {currentWord && currentWord.phonetic ? currentWord.phonetic : '\u00A0'}
              </span>
            </div>

            {/* 翻译显示 */}
            <div className={`dictation-translation ${showChinese ? 'visible' : 'hidden'}`}>
              <span className="translation-text">
                {currentWord && currentWord.en_title ? currentWord.en_title : '\u00A0'}
              </span>
            </div>

            {/* 音频播放器 */}
            <div className="dictation-audio-player">
              <button 
                className={`play-button ${isPlaying ? 'playing' : ''}`}
                disabled={isPlaying}
                onClick={() => {
                  if (currentWord && !isPlaying) {
                    playWordAudio(currentWord, dictationSettings);
                  }
                }}
              >
                {isPlaying ? (
                  <div className="audio-playing">
                    <div className="sound-wave">
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                    </div>
                  </div>
                ) : (
                  <img 
                    src={playIcon} 
                    alt="播放" 
                    className="play-icon"
                  />
                )}
              </button>
              <span className="play-text">{isPlaying ? '播放中...' : '点击播放'}</span>
            </div>
          </div>
        </div>

        {/* 第二部分 - 控制按钮区域 (12%) */}
        <div className="dictation-section-2">
          <div className="dictation-controls">
            {/* 左侧控制按钮区 */}
            <div className="controls-left">
              <button 
                className="control-btn prev-btn"
                onClick={prevWord}
                disabled={currentWordIndex === 0}
                title="上一个单词"
              >
                上一个
              </button>
              
              <button 
                className={`control-btn auto-play-btn ${isAutoPlaying ? 'active' : ''}`}
                onClick={toggleAutoPlay}
                title={isAutoPlaying ? '停止自动播放' : '开始自动播放'}
              >
                <div className="auto-play-icon">
                  {isAutoPlaying ? (
                    // 暂停图标：两条竖线
                    <>
                      <div className="pause-bar"></div>
                      <div className="pause-bar"></div>
                    </>
                  ) : (
                    // 播放图标：右三角形
                    <div className="play-triangle"></div>
                  )}
                </div>
              </button>
              
              {currentWordIndex === selectedWords.length - 1 ? (
                <button 
                  className="control-btn settle-btn"
                  onClick={handleSettle}
                  title="结算"
                >
                  结算
                </button>
              ) : (
                <button 
                  className="control-btn next-btn"
                  onClick={nextWord}
                  title="下一个单词"
                >
                  下一个
                </button>
              )}
              
              <button 
                className="control-btn settings-btn"
                onClick={handleOpenDictationSettings}
                title="听写设置"
              >
                听写设置
              </button>
            </div>
            
            {/* 中间开关按钮区 */}
            <div className="toggle-switch-group">
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showChinese ? 'active' : ''}`}
                  onClick={handleToggleChinese}
                  title="显示/隐藏中文释义"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">中文</span>
              </label>
              
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showPhonetic ? 'active' : ''}`}
                  onClick={handleTogglePhonetic}
                  title="显示/隐藏音标"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">音标</span>
              </label>
            </div>
            
            {/* 右侧功能按钮区 */}
            <div className="controls-right">
              <button 
                className="control-btn back-btn"
                onClick={handleBack}
                title="返回单词选择页面"
              >
                返回同步词汇
              </button>
            </div>
          </div>
        </div>

        {/* 结算画面 */}
        {showResults && !showAnswers && (
          <div className="dictation-results-overlay" onClick={handleCloseResults}>
            <div className="dictation-results-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="results-title">听写完成！</h2>
              <p className="results-summary">
                已完成 {selectedWords.length} 个单词的听写
              </p>
              <div className="results-actions">
                <button 
                  className="result-btn show-answers-btn"
                  onClick={handleShowAnswers}
                >
                  展示答案
                </button>
                <button 
                  className="result-btn retry-btn"
                  onClick={handleRetry}
                >
                  重新听写
                </button>
                <button 
                  className="result-btn back-vocab-btn"
                  onClick={handleBack}
                >
                  返回同步词汇
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 答案展示页面 */}
        {showAnswers && (
          <div className="dictation-answers-page">
            {/* Logo区域 */}
            <div className="dictation-header">
              <img 
                src={logoImage} 
                alt="大鱼人机口语" 
                className="dictation-logo"
              />
            </div>
            
            {/* 第一部分 - 答案展示区域 (88%) */}
            <div className="dictation-section-1">
              <div className="answers-display-area">
                {selectedWords.map((word, index) => (
                  <div key={index} className="answer-word-item">
                    <div className="answer-word-number">{index + 1}</div>
                    <div className="answer-word-text">{word.title}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 第二部分 - 按钮区域 (12%) */}
            <div className="dictation-section-2">
              <div className="dictation-controls">
                <div className="controls-center">
                  <button 
                    className="control-btn answers-retry-btn"
                    onClick={() => {
                      handleCloseAnswers();
                      handleRetry();
                    }}
                  >
                    重新听写
                  </button>
                  <button 
                    className="control-btn answers-back-btn"
                    onClick={handleBack}
                  >
                    返回同步词汇
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 听写设置弹窗 */}
        <SettingsModal
          isOpen={showDictationSettings}
          onClose={handleDictationSettingsClose}
          onStart={handleDictationSettingsUpdate}
          title={dictationConfig.title}
          settingItems={dictationConfig.settingItems}
          defaultSettings={dictationSettings}
        />
      </div>
  );
};

export default Dictation;
